"use client"

import { useState, useEffect } from "react"
import Swal from "sweetalert2"
import { Form, Button, Spinner, Table, ButtonGroup, Row, Col, InputGroup } from "react-bootstrap"
import Sidebar from "./Sidebar"
import DashNavbar from "./DashNavbar"
import Select from "react-select"
import axios from "axios"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"

const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/"

const RATING_SCALE_OPTIONS = [
  { value: "Exceptional", label: "Exceptional", numeric: 5 },
  { value: "Above Average", label: "Above Average", numeric: 4 },
  { value: "Average", label: "Average", numeric: 3 },
  { value: "Satisfactory", label: "Satisfactory", numeric: 2 },
  { value: "Unsatisfactory", label: "Unsatisfactory", numeric: 1 },
]

const RECOMMENDATION_OPTIONS = [
  { value: "STRONGLY_RECOMMEND", label: "Strongly Recommend" },
  { value: "RECOMMEND", label: "Recommend" },
  { value: "NEUTRAL", label: "Neutral" },
  { value: "DO_NOT_RECOMMEND", label: "Do Not Recommend" },
]

const initialRecommendationState = RECOMMENDATION_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = false;
    return acc;
}, {});

export default function EvaluationForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [rawInterviews, setRawInterviews] = useState([]);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [opportunityOptions, setOpportunityOptions] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunityType, setOpportunityType] = useState('');
  const [postingId, setPostingId] = useState('');
  const [evaluationQuestions, setEvaluationQuestions] = useState([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
  const [formData, setFormData] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [evaluationMode, setEvaluationMode] = useState('general'); 
  const [strengths, setStrengths] = useState(['']);
  const [developmentAreas, setDevelopmentAreas] = useState(['']);
  const [finalComments, setFinalComments] = useState("");
  const [recommendations, setRecommendations] = useState(initialRecommendationState);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [existingGeneralEvalId, setExistingGeneralEvalId] = useState(null);
  const [existingParamEvalId, setExistingParamEvalId] = useState(null);

  const resetFormState = () => {
    setEvaluationQuestions([]);
    setFormData({});
    setStrengths(['']);
    setDevelopmentAreas(['']);
    setFinalComments('');
    setRecommendations(initialRecommendationState);
    setIsReadOnly(false);
    setExistingGeneralEvalId(null);
    setExistingParamEvalId(null);
  }

  const formatOpportunityType = (typeString) => {
    if (!typeString) return "Opportunity";
    return typeString.replace(/_/g, ' ');
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch ALL interviews from the general endpoint
        const interviewsResponse = await axios.get(`${API_BASE_URL}interviews/`);
        const allInterviews = Array.isArray(interviewsResponse.data) ? interviewsResponse.data : [interviewsResponse.data];
        setRawInterviews(allInterviews);
        
        if (allInterviews.length > 0) {
          const uniqueMap = new Map();
          allInterviews.forEach(interview => {
             if (interview.candidate_id && !uniqueMap.has(interview.candidate_id)) {
                 uniqueMap.set(interview.candidate_id, {
                     value: interview.candidate_id,
                     label: `${interview.candidate} (ID: ${interview.candidate_id})`,
                     candidate_name: interview.candidate,
                     candidate_email: interview.candidate_email
                 });
             }
          });
          setCandidateOptions(Array.from(uniqueMap.values()));
        }
      } catch (error) {
        Swal.fire("Error", `Failed to fetch interviews: ${error.message}`, "error");
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    setOpportunityOptions([]);
    setSelectedOpportunity(null);
    setOpportunityType('');
    setPostingId('');
    resetFormState();

    if (selectedCandidate && rawInterviews.length > 0) {
        const candidateId = selectedCandidate.value;
        const specificInterviews = rawInterviews.filter(item => 
            item.candidate_id === candidateId
        );
        
        const opts = specificInterviews.map(interview => {
            const typeLabel = formatOpportunityType(interview.opening_post);
            const label = `${interview.job_title} (${typeLabel})`;
            return {
                value: interview.id,
                label: label,
                original_title: interview.job_title,
                opening_post: interview.opening_post,
                opening_post_id: interview.opening_post_id,
                ...interview
            };
        });
        setOpportunityOptions(opts);
    }
  }, [selectedCandidate, rawInterviews]);

useEffect(() => {
  if (!selectedOpportunity) {
    resetFormState();
    return;
  }

  const fetchQuestionsAndExistingData = async () => {
    setIsLoadingQuestions(true);
    resetFormState();
    
    const jobType = selectedOpportunity.opening_post;
    const interviewId = selectedOpportunity.value;
    const postingIdFromInterview = selectedOpportunity.opening_post_id;
    
    // Set display values
    setOpportunityType(formatOpportunityType(jobType));
    setPostingId(postingIdFromInterview || 'N/A');

    console.log("Selected Opportunity:", selectedOpportunity);
    console.log("Posting ID from interview:", postingIdFromInterview);
    console.log("Evaluation Mode:", evaluationMode);

    try {
      let rawQuestions = [];
      
      if (evaluationMode === 'general') {
        // For general mode - fetch template
        try {
          console.log("Fetching general template for job type:", jobType);
          const templateResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
          const allEvaluations = templateResponse.data || [];
          console.log("All templates:", allEvaluations);
          
          const template = allEvaluations.find(item => 
            item.candidate_name === "N/A - Template" && 
            item.job_type === jobType
          );

          console.log("Found template:", template);

          if (template && template.evaluation_details) {
            rawQuestions = template.evaluation_details.map((d, index) => ({
              id: `gen_${index}`,
              question_text: d.question_text || "General Question",
              area: "General Questions",
              answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
            }));
            console.log("General questions loaded:", rawQuestions);
          }
        } catch (error) {
          console.error("Error fetching general template:", error);
        }
      } else { 
        // For parameters mode
        if (postingIdFromInterview) {
          try {
            console.log(`Fetching parameters from: ${API_BASE_URL}job-postings/${postingIdFromInterview}/evaluation-parameters/`);
            const response = await axios.get(`${API_BASE_URL}job-postings/${postingIdFromInterview}/evaluation-parameters/`);
            const params = response.data || [];
            console.log("Parameters API response:", params);

            // Filter parameters to only include those that match the postingIdFromInterview
            const filteredParams = params.filter(p => 
              p.is_active !== false && 
              p.linked_posting_id === postingIdFromInterview
            );

            console.log("Filtered parameters for posting ID", postingIdFromInterview, ":", filteredParams);

            rawQuestions = filteredParams.map(p => {
              let areaLabel = p.area || "Specific Parameters";
              if (p.area === "BEHAVIORAL") areaLabel = "Additional Behaviour & Functional Parameters";
              else if (p.area === "ROLE_FIT") areaLabel = "Role-Specific & Cultural Fit";
              else if (p.area === "CORE_COMPETENCIES") areaLabel = "Core Competencies";

              return {
                id: `param_${p.id}`, 
                original_id: p.id,
                question_text: p.parameter_text || "Parameter",
                description: p.description || "", 
                area: areaLabel, 
                answers: RATING_SCALE_OPTIONS.map(opt => opt.value)
              };
            });
            console.log("Processed questions:", rawQuestions);
          } catch (error) {
            console.error("Error fetching parameters:", error);
            console.error("Error details:", error.response?.data || error.message);
          }
        }
      }
      
      setEvaluationQuestions(rawQuestions);

      // Check for existing evaluations in BOTH modes
      try {
        let isReadOnlyMode = false;
        let newFormData = {};
        
        if (evaluationMode === 'general') {
          // Check for existing general evaluation
          console.log(`Checking for existing general evaluation for candidate: ${selectedCandidate.candidate_name}, job: ${postingIdFromInterview}`);
          const generalResponse = await axios.get(`${API_BASE_URL}save_evaluation/`);
          const allGeneralEvals = generalResponse.data || [];
          
          // Find evaluation for this candidate and posting ID
    const existingGeneralEval = allGeneralEvals.find(evaluation => 
      evaluation.candidate_name === selectedCandidate.candidate_name && 
      evaluation.job_posting?.toLowerCase() === postingIdFromInterview?.toLowerCase() &&
      evaluation.candidate_name !== "N/A - Template" // Exclude templates
    );

          console.log("Found existing general evaluation:", existingGeneralEval);

          if (existingGeneralEval) {
            isReadOnlyMode = true;
            setExistingGeneralEvalId(existingGeneralEval.id);
            
            // Load existing general evaluation data
            if (existingGeneralEval.evaluation_details) {
              existingGeneralEval.evaluation_details.forEach((detail, index) => {
                const ratingValue = RATING_SCALE_OPTIONS.find(opt => opt.numeric === detail.answer_value)?.value || "";
                newFormData[`question_gen_${index}_answer`] = ratingValue;
              });
            }
          }
        } else {
          // Check for existing parameter evaluation
          console.log(`Checking for existing parameter evaluation at: ${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`);
          try {
            const paramResponse = await axios.get(`${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`);
            if (paramResponse.data && Object.keys(paramResponse.data).length > 0) {
              isReadOnlyMode = true;
              setExistingParamEvalId(paramResponse.data.id);
              
              const existingData = paramResponse.data;
              
              // Load existing parameter evaluation data
              if (existingData.responses) {
                existingData.responses.forEach(resp => {
                  // Find the matching question to get the correct ID
                  const matchingQuestion = rawQuestions.find(q => q.original_id === resp.parameter);
                  if (matchingQuestion) {
                    const matchedOpt = RATING_SCALE_OPTIONS.find(o => o.numeric === resp.rating);
                    newFormData[`question_${matchingQuestion.id}_answer`] = matchedOpt ? matchedOpt.value : "";
                    newFormData[`question_${matchingQuestion.id}_comments`] = resp.comments || "";
                  }
                });
                
                // Set strengths, development areas, final comments
                if (existingData.strengths && Array.isArray(existingData.strengths)) {
                  setStrengths(existingData.strengths.length > 0 ? existingData.strengths : ['']);
                }
                if (existingData.development_areas && Array.isArray(existingData.development_areas)) {
                  setDevelopmentAreas(existingData.development_areas.length > 0 ? existingData.development_areas : ['']);
                }
                setFinalComments(existingData.final_comments || "");
                
                // Set recommendations
                if (existingData.overall_recommendation) {
                  const recState = { ...initialRecommendationState };
                  recState[existingData.overall_recommendation] = true;
                  setRecommendations(recState);
                }
              }
            }
          } catch (e) {
            console.log("No existing parameter evaluation found:", e.message);
          }
        }
        
        setIsReadOnly(isReadOnlyMode);
        if (isReadOnlyMode) {
          console.log("Loaded form data:", newFormData);
          setFormData(newFormData);
        }
      } catch (error) {
        console.error("Error checking existing evaluations:", error);
      }
    } catch (error) {
      console.error("Error in fetchQuestionsAndExistingData:", error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };
  
  fetchQuestionsAndExistingData();
}, [selectedOpportunity, evaluationMode]);

  const handleCandidateSelect = (option) => {
    setSelectedCandidate(option);
  };

  const handleOpportunitySelect = (option) => {
    setSelectedOpportunity(option);
    setEvaluationMode('general');
    if (option) {
        setOpportunityType(formatOpportunityType(option.opening_post));
        setPostingId(option.opening_post_id || 'N/A');
    } else {
        setOpportunityType('');
        setPostingId('');
    }
  };

const handleModeChange = (mode) => {
  // Always allow switching between modes to view data
  setEvaluationMode(mode);
  // Reset form data when switching modes
  setFormData({});
  // Reset readOnly flag - it will be set again in useEffect based on existing data
  setIsReadOnly(false);
  setStrengths(['']);
  setDevelopmentAreas(['']);
  setFinalComments('');
  setRecommendations(initialRecommendationState);
};

  const handleQuestionChange = (id, fieldType, value) => {
    if (isReadOnly) return;
    setFormData(prev => ({ ...prev, [`question_${id}_${fieldType}`]: value }));
  };

  const handleDynamicListChange = (setter, index, value) => {
    if (isReadOnly) return;
    setter(prevList => {
      const newList = [...prevList];
      newList[index] = value;
      return newList;
    });
  };
  
  const addDynamicListItem = (setter) => !isReadOnly && setter(prev => [...prev, '']);
  const removeDynamicListItem = (setter, index) => !isReadOnly && setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) {
      Swal.fire("Info", "This evaluation has already been submitted and cannot be modified.", "info");
      return;
    }
    
    if (!selectedCandidate || !selectedOpportunity) {
      Swal.fire("Warning", "Please select both a candidate and an opportunity.", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let submissionUrl = "";
      let payload = {};
      const ratingMap = RATING_SCALE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.value]: opt.numeric }), {});
      const postingIdFromInterview = selectedOpportunity.opening_post_id;
      const jobType = selectedOpportunity.opening_post;

      if (evaluationMode === 'general') {
        // For general mode - use PUT if updating, POST if creating
        if (existingGeneralEvalId) {
          submissionUrl = `${API_BASE_URL}save_evaluation/${existingGeneralEvalId}/`;
          // PUT method for update
          payload = {
            candidate_name: selectedCandidate.candidate_name,
            candidate_email: selectedCandidate.candidate_email,
            interviewer_email: localStorage.getItem("email") || "unknown@example.com",
            job_type: jobType,
            job_posting_id: postingIdFromInterview,
            evaluation_details: evaluationQuestions.map(q => ({
              question_text: q.question_text,
              answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
            }))
          };
          await axios.put(submissionUrl, payload);
        } else {
          submissionUrl = `${API_BASE_URL}save_evaluation/`;
          payload = {
            candidate_name: selectedCandidate.candidate_name,
            candidate_email: selectedCandidate.candidate_email,
            interviewer_email: localStorage.getItem("email") || "unknown@example.com",
            job_type: jobType,
            job_posting_id: postingIdFromInterview,
            evaluation_details: evaluationQuestions.map(q => ({
              question_text: q.question_text,
              answer_value: ratingMap[formData[`question_${q.id}_answer`]] || null
            }))
          };
          await axios.post(submissionUrl, payload);
        }
      } else { 
        const selectedRecs = Object.keys(recommendations).filter(key => recommendations[key]);
        if (selectedRecs.length === 0) {
            Swal.fire("Validation Error", "Please select at least one overall recommendation.", "warning");
            setIsSubmitting(false);
            return;
        }
        const interviewId = selectedOpportunity.value; 
        
        // For parameter mode - use PUT if updating, POST if creating
        if (existingParamEvalId) {
          submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/${existingParamEvalId}/`;
          payload = {
            posting_type: jobType,
            posting_id: postingIdFromInterview,
            overall_recommendation: selectedRecs[0], 
            strengths: strengths.map(s => s.trim()).filter(s => s),
            development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
            final_comments: finalComments,
            responses: evaluationQuestions.map(q => ({
              parameter: q.original_id,
              rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
              comments: formData[`question_${q.id}_comments`] || "",
            })),
          };
          await axios.put(submissionUrl, payload);
        } else {
          submissionUrl = `${API_BASE_URL}interviews/${interviewId}/submit-evaluation/`;
          payload = {
            posting_type: jobType,
            posting_id: postingIdFromInterview,
            overall_recommendation: selectedRecs[0], 
            strengths: strengths.map(s => s.trim()).filter(s => s),
            development_areas: developmentAreas.map(d => d.trim()).filter(d => d),
            final_comments: finalComments,
            responses: evaluationQuestions.map(q => ({
              parameter: q.original_id,
              rating: ratingMap[formData[`question_${q.id}_answer`]] || null,
              comments: formData[`question_${q.id}_comments`] || "",
            })),
          };
          await axios.post(submissionUrl, payload);
        }
      }
      
      console.log("Submitting payload:", payload);
      setIsReadOnly(true);
      Swal.fire("Success", `Evaluation ${existingGeneralEvalId || existingParamEvalId ? "updated" : "submitted"} successfully!`, "success");
    } catch (error) {
      console.error("Submission error:", error);
      const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : (error.message || "An unknown error occurred.");
      Swal.fire("Submission Error", `Failed to submit: ${errorMessage}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  }

const renderTableFormControl = (question) => {
  const qId = question.id;
  const answerKey = `question_${qId}_answer`;
  const commentsKey = `question_${qId}_comments`;

  return {
    rating: (
      <Form.Control as="select" 
          value={formData[answerKey] || ""} 
          onChange={(e) => handleQuestionChange(qId, "answer", e.target.value)} 
          required 
          disabled={isReadOnly}
          size="sm">
        <option value="">-- Select Rating --</option>
        {RATING_SCALE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </Form.Control>
    ),
    comments: (
      <Form.Control as="textarea" 
          rows={2} 
          value={formData[commentsKey] || ""} 
          onChange={(e) => handleQuestionChange(qId, "comments", e.target.value)} 
          onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          placeholder="Comments..." 
          disabled={isReadOnly}
          style={{ overflow: 'hidden', resize: 'none' }}
          size="sm"/>
    ),
  };
};

  const groupedQuestions = evaluationQuestions.reduce((groups, q) => {
    const area = q.area || "General Questions";
    if (!groups[area]) groups[area] = [];
    groups[area].push(q);
    return groups;
  }, {});

  return (
    <>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="main">
          <div className="container-fluid p-3 d-flex flex-column h-100">
            <div className="card shadow-sm flex-grow-1 d-flex flex-column h-100">
              <div className="card-header bg-primary text-white">
                <h2 className="mb-0 h4">Candidate Interview Evaluation {isReadOnly && <span className="badge bg-warning text-dark ms-2">Submitted</span>}</h2>
              </div>
              <div className="card-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                    ``````````````  <Form.Group controlId="candidateSelection" className="mb-4">
                        <Form.Label className="fw-bold">1. Select Candidate:</Form.Label>
                        <Select options={candidateOptions} value={selectedCandidate} onChange={handleCandidateSelect} placeholder="Search..." isLoading={isLoading} isDisabled={isLoading || isSubmitting} isClearable />
                      </Form.Group>
                    </Col>

                    {selectedCandidate && (
                      <Col md={6}>
                        <Form.Group controlId="opportunitySelection" className="mb-4">
                          <Form.Label className="fw-bold">2. Opportunity for this Interview:</Form.Label>
                          <Select options={opportunityOptions} value={selectedOpportunity} onChange={handleOpportunitySelect} placeholder="Select interview..." isDisabled={isSubmitting} isClearable />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                  
                  {selectedOpportunity && (
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="opportunityType" className="mb-4">
                          <Form.Label className="fw-bold">3. Opportunity Type:</Form.Label>
                          <Form.Control type="text" value={opportunityType} readOnly disabled className="bg-light" />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group controlId="postingId" className="mb-4">
                          <Form.Label className="fw-bold">4. Posting ID:</Form.Label>
                          <Form.Control type="text" value={postingId} readOnly disabled className="bg-light" />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {selectedOpportunity && (
                    <div className="text-center mb-4">
                      <ButtonGroup>
                        <Button 
                          variant={evaluationMode === 'general' ? 'primary' : 'outline-primary'} 
                          onClick={() => handleModeChange('general')} 
                          disabled={isLoadingQuestions || isSubmitting}>
                          Interview Questions
                        </Button>
                        <Button 
                          variant={evaluationMode === 'parameters' ? 'primary' : 'outline-primary'} 
                          onClick={() => handleModeChange('parameters')} 
                          disabled={isLoadingQuestions || isSubmitting}>
                          Post Interview Evaluation
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}

                  {isLoadingQuestions && <div className="text-center my-4"><Spinner animation="border" variant="primary" /></div>}

                  {!isLoadingQuestions && selectedOpportunity && (
                    <>
                      {evaluationQuestions.length > 0 ? (
                        Object.entries(groupedQuestions).map(([area, questions]) => (
                          <div key={area} className="mb-5">
                            <h4 className="mb-3 text-primary text-capitalize border-bottom pb-2">{area.replace(/_/g, ' ')}</h4>
                            <Table striped bordered hover responsive className="align-middle">
                              <thead className="table-dark">
                                <tr>
                                  <th style={{ width: "5%" }} className="text-center">Sr. No</th>
                                  <th style={{ width: evaluationMode === 'parameters' ? "35%" : "60%" }}>Parameter</th>
                                  {evaluationMode === 'parameters' && <th style={{ width: "25%" }}>Description</th>}
                                  <th style={{ width: "15%" }}>Rating</th>
                                  <th style={{ width: "20%" }}>Comments</th>
                                </tr>
                              </thead>
                              <tbody>
                                {questions.map((q, qIndex) => {
                                  const controls = renderTableFormControl(q);
                                  return (
                                    <tr key={q.id}>
                                      <td className="text-center fw-bold">{qIndex + 1}</td>
                                      <td className="text-start">{q.question_text}</td>
                                      {evaluationMode === 'parameters' && <td className="small text-muted">{q.description || "-"}</td>}
                                      <td>{controls.rating}</td>
                                      <td>{controls.comments}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        ))
                      ) : (
                        <div className="alert alert-info text-center">
                          {evaluationMode === 'general' 
                            ? "No general evaluation questions found for this opportunity type." 
                            : `No evaluation parameters found for posting ID: ${postingId}. Please check if parameters are configured.`}
                        </div>
                      )}
                      
                      {evaluationMode === 'parameters' && evaluationQuestions.length > 0 && (
                        <div className="p-4 border rounded bg-light mt-4 shadow-sm">
                            <h4 className="mb-3 text-primary border-bottom pb-2">Final Summary</h4>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <Form.Label className="fw-bold">Strengths</Form.Label>
                                    {strengths.map((strength, index) => (
                                        <InputGroup key={index} className="mb-2">
                                            <Form.Control value={strength} onChange={e => handleDynamicListChange(setStrengths, index, e.target.value)} disabled={isReadOnly} />
                                            {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setStrengths, index)} disabled={strengths.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
                                        </InputGroup>
                                    ))}
                                    {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setStrengths)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Strength</Button>}
                                </Col>
                                <Col md={6} className="mb-3">
                                    <Form.Label className="fw-bold">Areas for Development</Form.Label>
                                     {developmentAreas.map((area, index) => (
                                        <InputGroup key={index} className="mb-2">
                                            <Form.Control value={area} onChange={e => handleDynamicListChange(setDevelopmentAreas, index, e.target.value)} disabled={isReadOnly} />
                                            {!isReadOnly && <Button variant="outline-danger" onClick={() => removeDynamicListItem(setDevelopmentAreas, index)} disabled={developmentAreas.length <= 1}><FontAwesomeIcon icon={faTrash} /></Button>}
                                        </InputGroup>
                                    ))}
                                    {!isReadOnly && <Button variant="outline-primary" size="sm" onClick={() => addDynamicListItem(setDevelopmentAreas)}><FontAwesomeIcon icon={faPlus} className="me-2"/> Add Area</Button>}
                                </Col>
                            </Row>
                             <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Final Comments</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    value={finalComments} 
                                    onChange={e => setFinalComments(e.target.value)} 
                                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                    disabled={isReadOnly}
                                    style={{ overflow: 'hidden', resize: 'none' }}
                                />
                            </Form.Group>
                             <Form.Group>
                                <Form.Label className="fw-bold d-block">Overall Recommendation <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex flex-wrap gap-3">
                                {RECOMMENDATION_OPTIONS.map(opt => (
                                    <Form.Check key={opt.value} type="radio" id={`rec-${opt.value}`} label={opt.label} name="recommendation" checked={recommendations[opt.value]} disabled={isReadOnly} onChange={() => setRecommendations({ ...initialRecommendationState, [opt.value]: true })} />
                                ))}
                                </div>
                            </Form.Group>
                        </div>
                      )}
                      
                      <div className="text-center mt-4 pb-5">
                        {evaluationQuestions.length > 0 && (
                          <Button variant="success" type="submit" size="lg" className="px-5" disabled={isSubmitting || isLoadingQuestions}>
                            {isSubmitting ? <Spinner size="sm" animation="border" /> : isReadOnly ? "Update Evaluation" : "Submit Evaluation"}
                          </Button>
                        )}
                        {isReadOnly && <div className="alert alert-warning d-inline-block px-5 mt-3">This evaluation has already been submitted. You can update it if needed.</div>}
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}